import Highcharts from "highcharts";
import { z } from "zod";

import {
  ALL_BANDS,
  bandDisplayName,
  bandFor,
  fadeBandColor,
  formatScore,
} from "../sentimentScoreBands";
import {
  hoverLabel,
  resolveAxisTitles,
  resolveFieldLabels,
  unitFrom,
} from "../data/payloadLabels";

// ---------------------------------------------------------------------------
// sentiment_scatter — one dot per (x, y) bubble, colored by average sentiment.
//
// The payload is an aggregated scatter, not raw calls: the backend has already
// grouped calls that share an (x, y) pair, so `count` is how many calls sit in
// that dot and `score` is their average sentiment (metadata.score_scale:
// "0 = negative .. 1 = positive"). That's why the dot area encodes count and
// the color encodes score — neither is on an axis.
//
// Shape accepted (both, because the registry conversion path can hand over the
// bare array while the dashboard hands over the whole envelope):
//   { data: [{ x, y, count, score }], metadata: {...} }
//   [{ x, y, count, score }]
// ---------------------------------------------------------------------------

// Rows are validated one at a time (below) rather than as an array, so a single
// malformed row from the API drops that dot instead of blanking the widget.
const SentimentPointSchema = z.object({
  x: z.number(),
  y: z.number(),
  // Both optional on purpose: a payload without them still plots as a plain
  // scatter (default size, "unscored" band) instead of throwing the row away.
  count: z.number().optional(),
  score: z.number().optional(),
  // The backend's own wording for this point's sentiment ("Positive lean",
  // "Neutral / Mixed", …). Preferred over our band name in the hover so the
  // chart speaks the same vocabulary as the rest of the product.
  sentiment_label: z.string().nullable().optional(),
});

type SentimentPoint = z.infer<typeof SentimentPointSchema>;

// Axis titles come from the backend (see resolveAxisTitles) — the numbers alone
// don't say what they measure, and this renderer must not invent a meaning for
// them. Kept out of this schema so both key spellings stay in one place.
const MetadataSchema = z.object({
  bubble_count: z.number().optional(),
  total_calls: z.number().optional(),
  // True when the backend hit max_bubbles and cut the tail. Surfaced in the
  // subtitle: a capped dataset must not read as the complete picture.
  truncated: z.boolean().optional(),
  max_bubbles: z.number().optional(),
  score_scale: z.string().optional(),
});

type SentimentMetadata = z.infer<typeof MetadataSchema>;

// metadata stays `unknown` here and is validated separately below: zod strips
// keys a schema doesn't declare, which would silently swallow the axis-label
// keys before resolveAxisTitles ever sees them.
const EnvelopeSchema = z.object({
  data: z.array(z.unknown()).optional(),
  metadata: z.unknown().optional(),
});

// ── Dot size ────────────────────────────────────────────────────────────────
// Radius grows with the square root of count so the dot's AREA is proportional
// to the number of calls in it — the honest encoding. Singletons stay small
// (most of this payload is count 1) and the cap stops a heavy bubble from
// swallowing its neighbours.
const RADIUS_FOR_ONE = 3;
const MAX_RADIUS = 12;

const radiusFor = (count: number | undefined): number => {
  const calls = typeof count === "number" && count > 0 ? count : 1;
  return Math.min(RADIUS_FOR_ONE * Math.sqrt(calls), MAX_RADIUS);
};

const formatInt = (value: number): string => value.toLocaleString();

// An axis title, or `text: null` to remove it. Typed structurally rather than as
// Highcharts' XAxisTitleOptions/YAxisTitleOptions because those two disagree
// about whether `text` may be null — see the yAxis call site.
type AxisTitle = { text: string | null; style?: Highcharts.CSSObject };

const axisTitle = (text: string | null): AxisTitle =>
  text
    ? { text, style: { color: "#64748B", fontSize: "11px" } }
    : { text: null };

type ParsedPayload = {
  points: SentimentPoint[];
  metadata: SentimentMetadata;
  /** Untouched, so the label resolver can read keys this file doesn't declare. */
  rawMetadata: unknown;
};

const parsePayload = (data: unknown): ParsedPayload => {
  const envelope = Array.isArray(data)
    ? undefined
    : EnvelopeSchema.safeParse(data).data;
  const rawRows: unknown[] = Array.isArray(data)
    ? data
    : (envelope?.data ?? []);
  const rawMetadata = envelope?.metadata;

  const points = rawRows.reduce<SentimentPoint[]>((acc, row) => {
    const parsed = SentimentPointSchema.safeParse(row);
    if (parsed.success) acc.push(parsed.data);
    return acc;
  }, []);

  return {
    points,
    metadata: MetadataSchema.safeParse(rawMetadata).data ?? {},
    rawMetadata,
  };
};

// Counts line, plus a truncation warning when the backend capped the payload.
const buildSubtitle = (
  points: SentimentPoint[],
  metadata: SentimentMetadata,
): string => {
  const dots = metadata.bubble_count ?? points.length;
  const calls =
    metadata.total_calls ?? points.reduce((sum, p) => sum + (p.count ?? 1), 0);

  const counts =
    `<span style="color:#64748B;">${formatInt(dots)} points · ` +
    `${formatInt(calls)} calls</span>`;

  if (!metadata.truncated) return counts;

  // Say it plainly rather than let a capped chart look complete.
  const cap = metadata.max_bubbles
    ? ` at ${formatInt(metadata.max_bubbles)}`
    : "";
  return (
    `${counts} <span style="color:#B45309;font-weight:600;">` +
    `· truncated${cap}, not all calls shown</span>`
  );
};

const buildSentimentScatterOptions = (
  data: unknown,
  spec: unknown,
): Highcharts.Options => {
  const { points, metadata, rawMetadata } = parsePayload(data);

  // Backend-provided titles only; no invented fallback. When neither the payload
  // nor the spec sends one the axis simply goes untitled.
  const { x: xTitle, y: yTitle } = resolveAxisTitles(rawMetadata, spec);
  const labels = resolveFieldLabels(rawMetadata);

  // Hover row labels, kept short on purpose. field_labels is where the backend
  // explains a field in full ("Calls with this exact duration + interaction
  // count"); a hover needs a name, not a sentence, so hoverLabel takes the first
  // wording that still reads as a label and otherwise falls back to a plain
  // noun. Any unit in a parenthetical moves to the value ("118 sec").
  // "Calls" stays plural whatever the count is — it labels the `count` field,
  // not the value in front of it, and a label that flips to "Call" on the many
  // single-call bubbles reads as if the number were hardcoded.
  const hoverLabels = {
    x: hoverLabel([labels.x, xTitle], "X"),
    y: hoverLabel([labels.y, yTitle], "Y"),
    count: hoverLabel([labels.count], "Calls"),
  };
  const hoverUnits = {
    x: unitFrom(labels.x, xTitle),
    y: unitFrom(labels.y, yTitle),
  };

  // One series per band → Highcharts draws the legend and gives click-to-filter
  // for free, and hovering a legend item dims the other bands.
  const series: Highcharts.SeriesScatterOptions[] = ALL_BANDS.map((band) => {
    const bandPoints = points.filter((p) => bandFor(p.score) === band);
    return { band, bandPoints };
  })
    // Bands with nothing in them are dropped so the legend only advertises
    // sentiment levels this payload actually contains.
    .filter(({ bandPoints }) => bandPoints.length > 0)
    .map(({ band, bandPoints }) => ({
      type: "scatter" as const,
      // Name the band the way the backend names it, so the legend and the hover
      // use one vocabulary ("Positive lean", not "Positive" in one place and
      // "Positive lean" in the other). Only taken when every point in the band
      // agrees — a disagreement means our thresholds and theirs have drifted
      // apart, and inventing a single name for the band would paper over that.
      name: bandDisplayName(band, bandPoints),
      color: band.color,
      marker: {
        symbol: "circle",
        // Semi-transparent fill so overlapping dots read as denser rather than
        // hiding each other, plus a faint dark ring — the light green band is
        // nearly invisible on white without it.
        fillColor: fadeBandColor(band.color, 0.72),
        lineWidth: 0.75,
        lineColor: "rgba(15, 23, 42, 0.35)",
      },
      data: bandPoints.map((p) => ({
        x: p.x,
        y: p.y,
        marker: { radius: radiusFor(p.count) },
        // The hover shows the raw count and score; the band word lives in the
        // legend and the dot color, so it isn't carried per point.
        custom: { count: p.count, score: p.score },
      })),
    }));

  return {
    chart: {
      type: "scatter",
      animation: false,
      // Drag a box to zoom. The x range is heavily skewed (most of this payload
      // sits in the first fifth of the axis while a few calls run far out), and
      // a log axis isn't available as a fix because y legitimately contains 0.
      zooming: { type: "xy" },
      spacingBottom: 12,
    },
    title: { text: "" },
    subtitle: {
      text: buildSubtitle(points, metadata),
      align: "left",
      useHTML: true,
      style: { fontSize: "11px" },
    },
    credits: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      title: axisTitle(xTitle),
      min: 0,
      startOnTick: false,
      endOnTick: false,
      gridLineWidth: 1,
      gridLineColor: "#F1F5F9",
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
      labels: { style: { color: "#64748B", fontSize: "10px" } },
    },
    yAxis: {
      // Cast because Highcharts types yAxis.title.text as string|undefined
      // while xAxis allows null — an inconsistency in their types, since null is
      // the documented way to remove either title. undefined can't be used
      // instead: on a y-axis it falls back to Highcharts' default, "Values".
      title: axisTitle(yTitle) as Highcharts.YAxisTitleOptions,
      min: 0,
      gridLineWidth: 1,
      gridLineColor: "#F1F5F9",
      labels: { style: { color: "#64748B", fontSize: "10px" } },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      symbolRadius: 6,
      itemStyle: { fontSize: "11px", fontWeight: "600", color: "#334155" },
      itemHoverStyle: { color: "#0F172A" },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      borderWidth: 0,
      borderRadius: 10,
      backgroundColor: "#0F172A",
      shadow: false,
      padding: 0,
      formatter() {
        const point = this as unknown as {
          x?: number;
          y?: number;

          point: {
            options: {
              custom?: { count?: number; score?: number };
            };
          };
        };
        const custom = point.point.options.custom ?? {};
        const calls = custom.count ?? 1;

        // white-space:normal is load-bearing, not decoration: Highcharts hard
        // sets white-space:nowrap on the element wrapping a useHTML tooltip
        // (HTMLElement#htmlCss), and the labels inherit it. Without overriding
        // it a long label can't wrap, so the row grows wider than the box
        // Highcharts measured and the text prints outside the dark card.
        const row = (label: string, value: string): string =>
          `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:14px;margin-top:2px;white-space:normal;">
             <span style="opacity:0.65;">${label}</span>
             <span style="font-weight:700;white-space:nowrap;">${value}</span>
           </div>`;

        const withUnit = (value: string, unit: string | null): string =>
          unit ? `${value} ${unit}` : value;

        // Just the score. The band word ("Mostly negative lean") is already
        // carried by the dot's color and the legend, so repeating it here only
        // widened the row — and "Average sentiment" from field_labels wrapped
        // onto a second line. Plain "Sentiment" plus the number.
        const sentimentValue =
          typeof custom.score === "number" ? formatScore(custom.score) : "—";
        // ${row("No. of calls", formatInt(calls))}

        return `
          <div style="padding:10px 12px;min-width:170px;max-width:240px;color:#f8fafc;font-size:12px;line-height:1.35;white-space:normal;">
            ${row(hoverLabels.x, withUnit(formatInt(point.x ?? 0), hoverUnits.x))}
            ${row(hoverLabels.y, withUnit(formatInt(point.y ?? 0), hoverUnits.y))}
            <div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);">
              ${row("Sentiment Score", sentimentValue)}
            </div>
          </div>
        `;
      },
    },
    plotOptions: {
      scatter: {
        // MUST stay disabled: Highcharts refuses object-form points past
        // turboThreshold (default 1000) with error #12, and this payload runs
        // to metadata.max_bubbles = 5000. Every point here is object-form
        // because each carries its own radius and custom fields.
        turboThreshold: 0,
        stickyTracking: false,
        states: {
          // Dim the other bands while one legend item is hovered.
          inactive: { opacity: 0.2 },
          hover: { halo: { size: 6 } },
        },
      },
    },
    // Same context-button placement as every other chart in the registry: the
    // hamburger sits in the widget header strip above the plot. ChartFactory
    // layers the menu items (exports + conversions) on top of this from
    // buildContextMenu, driven by the registry's exportItems.
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -20,
        },
      },
    },
    series,
  };
};

export function renderSentimentScatter(
  data: unknown,
  spec: unknown,
): Highcharts.Options {
  return buildSentimentScatterOptions(data, spec);
}
